import { Fragment } from 'react';
import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';

import { RichText } from '@preventx/prismic-services';
import { Box, Wrapper, Metaballs, Typography } from '@preventx/oxygen';

// Types
import { ActionLink } from '@/types/components/actions';

// Services
import { retrieveIcon } from '@/services/utils/icons';
import { applicationEnv } from '@/services/env/shared';

// Components
import { ActionLinks } from '@/components/Actions/ActionLinks';
import { RichTextHero } from '@/components/RichText/RichTextHero';

export type WithImagePlusHeroColumnsProps = {
  slice: Content.HeroSliceWithImageHeroColumns;
};

export const WithImagePlusHeroColumns = ({
  slice,
}: WithImagePlusHeroColumnsProps) => {
  const { primary } = slice;
  const { actions, title, image, lede, caption, hero_columns } = primary;

  const mappedActions = actions.map((item): ActionLink => ({
    action: item.action,
    addOutline: item.add_ghost_effect,
    icon: item?.icon !== 'None' ? retrieveIcon(item.icon) : undefined,
  }));

  const { WebsiteUrl } = applicationEnv();

  return (
    <Wrapper size="lg" addFlow>
      <Box component="div" className="hero__columns" addSwitcher>
        <Box component="div">
          <Box component="div" addFlow className="hero__content">
            <RichTextHero content={title} />

            <Typography component="p" className="lede">
              {asText(lede)}
            </Typography>

            {isFilled.richText(caption) && (
              <RichText
                content={caption}
                variant="default"
                websiteUrl={WebsiteUrl}
              />
            )}

            {actions && (
              <Box component="div" addCluster>
                <ActionLinks idPrefix="hero-actions" actions={mappedActions} />
              </Box>
            )}
          </Box>
        </Box>

        <Box component="div" className="hero-bg">
          {isFilled.image(image) && (
            <figure>
              <PrismicNextImage field={image} loading="eager" />
            </figure>
          )}

          {(hero_columns ?? []).map((heroColumn, index) => {
            const { type, standard_label, small_label } = heroColumn;
            const iteration = index + 1;

            const heroItemClass = `hero-item-${iteration} md:hidden-below`;

            return (
              <Fragment key={`hero-column-item-${iteration}`}>
                {/* Important : Type `None` is still embedded with markup in case something in the CSS decides 
                                to render stylised content */}
                {type === 'None' && (
                  <Box component="div" className={heroItemClass}></Box>
                )}

                {type === 'Metaballs' && (
                  <Metaballs className={heroItemClass} />
                )}

                {type === 'Sticker - Circle' && (
                  <Box
                    component="div"
                    className={`${heroItemClass} sticker-circle`}
                  >
                    <Typography component="p">
                      {asText(standard_label)}{' '}
                      <Typography component="small">
                        {asText(small_label)}
                      </Typography>
                    </Typography>
                  </Box>
                )}
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </Wrapper>
  );
};
